import { HttpLink } from 'apollo-angular/http';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ModelHelper } from '@dentsply-sirona/utilities';
import { TranslateService } from '@ngx-translate/core';

import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of } from 'rxjs';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/map';
import { filter, map } from 'rxjs/operators';
import { ConfigService } from 'src/app/modules/core/services/config.service';
import { LoaderService } from 'src/app/modules/core/services/loader.service';
import { NotifyOrConfirmType } from '../enums/notify-or-confirm-type.enum';
import { Operator } from '../enums/operator.enum';
import { Options } from '../enums/options';
import { Country } from '../models/country.model';
import { Language } from '../models/language.model';
import { ListRequest } from '../models/list-request.model';
import { Option } from '../models/option.model';
import { Region } from '../models/region.model';
import { Timezone } from '../models/timezone.model';
import { OptionsSchema } from '../schemas/options.schema';
import { GraphqlService } from './graphql.service';
import { ListService } from './list.service';
import { LoggerService } from './logger.service';
import { NotifyOrConfirmService } from './notify-or-confirm.service';
import { SessionService } from './session.service';
import { WindowRefService } from './window.service';
import { CfeValidators } from '../validators/validator';

/**
 * Service for communicating with the API in relation to options
 */
@Injectable({
	providedIn: 'root'
})
export class OptionsService extends GraphqlService {
	// Cached country options
	protected _countryOptions: Option[] = null;

	// Cached language options
	protected _languageOptions: Option[] = null;

	// Cached timezone options
	protected _listrangeOptions: Option[] = null;

	// Cached region options
	protected _regionOptions: { [countryCode: string]: Option[] } = {};

	// Cached timezone options
	protected _timezoneOptions: Option[] = null;

	constructor(
		protected http: HttpClient,
		protected httpLink: HttpLink,
		protected loaderService: LoaderService,
		protected configService: ConfigService,
		protected notifyOrConfirmService: NotifyOrConfirmService,
		protected sessionService: SessionService,
		protected router: Router,
		protected notification: NotifyOrConfirmService,
		protected translateService: TranslateService,
		protected listService: ListService,
		protected logger: LoggerService,
		protected ngZone: NgZone,
		protected toastrService: ToastrService,
		protected windowService: WindowRefService
	) {
		super(
			http,
			httpLink,
			configService,
			loaderService,
			notification,
			sessionService,
			translateService,
			router,
			logger,
			ngZone,
			toastrService,
			windowService
		);
	}

	/**
	 * Get country list
	 */
	getCountryList(): Observable<Country[]> {
		const countriesInChinaCloud = this.configService.config.countriesInChinaCloud;
		const hostedInChinaCloud = this.configService.config.hostedInChinaCloud;

		return this.listService
			.getList(
				ListRequest.map({
					id: 'countryList',
					properties: ['code', 'name', 'endOfRegistration']
				})
			)
			.pipe(map((data: any) => ModelHelper.mapArray<Country>(Country, data)))
			.pipe(
				map((countries) => {
					return _.filter(countries, (country) => {
						const countryInChinaCloud = countriesInChinaCloud.find((code) => code === country.code);
						return (hostedInChinaCloud && countryInChinaCloud) || (!hostedInChinaCloud && !countryInChinaCloud);
					});
				})
			);
	}

	/**
	 * Get country options
	 *
	 * @param refresh
	 */
	public getCountryOptions(refresh: boolean = false): Observable<Option[]> {
		// Use cache
		if (!refresh && this._countryOptions) {
			return of(OptionsService.sortByLabel(this.translateOptions(this._countryOptions)));
		}

		// Get country options from API
		return this.getCountryList().pipe(
			map((countryList) => {
				this._countryOptions = [];
				CfeValidators.countryRegistrationEndDates.clear();
				countryList.forEach((element) => {
					const option = new Option();
					option.id = element.code;
					option.label = element.name;
					this._countryOptions.push(option);

					CfeValidators.countryRegistrationEndDates.set(element.code, element.endOfRegistration);
				});

				return OptionsService.sortByLabel(this.translateOptions(this._countryOptions));
			})
		);
	}

	/**
	 * Sort list of objects by object.label
	 *
	 * @param items
	 */
	private static sortByLabel(items) {
		items.sort((a, b) => {
			if (a.label < b.label) {
				return -1;
			}
			if (a.label > b.label) {
				return 1;
			}
			return 0;
		});
		return items;
	}

	/**
	 * Get enum from GraphQL schema
	 *
	 * @param enumName
	 */
	public getEnum(enumName: string): Observable<string[]> {
		return this.query(OptionsSchema.getEnum, { enumName }).pipe(map((data: any) => data.__type.enumValues.map((obj) => obj.name)));
	}

	/**
	 * Get options
	 *
	 * @param options Array with names of options or Array with option and params object
	 * @param notifyOnError Whether notification should be shown on error
	 */
	public getOptions(options: (Options | { option: Options; params?: any[] })[], notifyOnError: boolean = true): Observable<Option[][]> {
		// Return one observable for all options
		return new Observable((subscriber) => {
			// Init
			const tasks$: Observable<any>[] = [];

			// Get options from API
			for (const option of options) {
				// Get option data
				let optionName: string;
				let optionParams: any[] = [];
				if (typeof option === 'string') {
					// Set option name
					optionName = option;
				} else {
					// Check option
					if (!option) {
						const optionError = new Error('Wrong element in options!');
						subscriber.error(optionError);
						throw optionError;
					}

					// Check params
					if (option.params && !Array.isArray(option.params)) {
						const optionError = new Error('Params of ' + option.option + 'must be an array!');
						subscriber.error(optionError);
						throw optionError;
					}

					// Set option data
					optionName = option.option;
					optionParams = option.params ? option.params : [];
				}

				// Check option function
				if (!this['get' + _.startCase(_.lowerCase(optionName)) + 'Options']) {
					const error = new Error('Missing Function get' + _.startCase(_.lowerCase(optionName)) + 'Options in OptionsService!');
					subscriber.error(error);
					throw error;
				}

				// Add option
				tasks$.push(this['get' + _.startCase(_.lowerCase(optionName)) + 'Options'](...optionParams));
			}

			if (tasks$.length === 0) {
				subscriber.next([]);
				subscriber.complete();
				return;
			}

			// Wait until als tasks are done
			forkJoin(tasks$).subscribe(
				(results) => {
					// Return results
					subscriber.next(results);
					subscriber.complete();

					// On error
				},
				(error) => {
					if (notifyOnError) {
						this.logger.error('Options could not be loaded:', error);
						this.notification.showNotification(
							this.translateService.instant('optionsService.note.error'),
							this.translateService.instant('optionsService.note.optionsCouldNotBeLoaded'),
							NotifyOrConfirmType.ERROR
						);
					}

					// Return error
					subscriber.error(error);
				}
			);
		});
	}

	/**
	 *	Returns the translatet option with id null
	 *
	 * @param option names of option
	 * @returns
	 */
	getNullOption(option: Options): Option {
		const nullOption = new Option();
		nullOption.id = null;

		switch (option) {
			case Options.LISTRANGE:
				nullOption.label = 'COMPANY_PRESETS';
				break;

			default:
				nullOption.label = '-';
				break;
		}

		return this.translateOptions([nullOption])[0];
	}

	/**
	 * Get language list
	 */
	getLanguageList(): Observable<Language[]> {
		return this.listService
			.getList(
				ListRequest.map({
					id: 'languageList',
					properties: ['code', 'name', 'displayName']
				})
			)
			.pipe(map((data: any) => ModelHelper.mapArray<Language>(Language, data)));
	}

	/**
	 * Get language options
	 */
	public getLanguageOptions(refresh: boolean = false): Observable<Option[]> {
		// Use cache
		if (!refresh && this._languageOptions) {
			return of(this.translateOptions(this._languageOptions));
		}

		// Get language options from API
		return this.getLanguageList().pipe(
			map((languageList) => {
				this._languageOptions = languageList.map((element) => {
					const option = new Option();
					option.id = element.code;
					option.label = element.displayName;
					return option;
				});

				return this.translateOptions(this._languageOptions);
			})
		);
	}

	/**
	 * Get list range options
	 *
	 * @param refresh
	 */
	public getListrangeOptions(refresh: boolean = false): Observable<Option[]> {
		// Use cache
		if (!refresh && this._listrangeOptions) {
			return of(this.translateOptions(this._listrangeOptions));
		}

		// Get listrange options from API
		return this.getEnum('Listrange').pipe(
			map((listrangeList) => {
				this._listrangeOptions = listrangeList.map((element) => {
					const option = new Option();
					option.id = element;
					option.label = element;
					return option;
				});

				return this.translateOptions(this._listrangeOptions);
			})
		);
	}

	/**
	 * Get timezone list
	 */
	getTimezoneList(): Observable<Timezone[]> {
		return this.query(OptionsSchema.getTimezoneList, {}).pipe(
			map((data: any) => ModelHelper.mapArray<Timezone>(Timezone, data.getTimeZoneList))
		);
	}

	/**
	 * Get timezone options
	 */
	public getTimezoneOptions(refresh: boolean = false): Observable<Option[]> {
		// Use cache
		if (!refresh && this._timezoneOptions) {
			return of(this._timezoneOptions);
		}

		// Get timezone options from API
		return this.getTimezoneList().pipe(
			map(
				(timezoneList) =>
					(this._timezoneOptions = timezoneList.map((element) => {
						const option = new Option();
						option.id = element.id;
						option.label = element.displayName;
						return option;
					}))
			)
		);
	}

	/**
	 * Get region list
	 */
	getRegionList(countryCode: string): Observable<Region[]> {
		// Check countryCode
		if (!countryCode) {
			return of([]);
		}

		return this.listService
			.getList(
				ListRequest.map({
					id: 'regionList',
					filter: {
						filters: [
							{
								propertyName: 'countryCode',
								operator: Operator.Equal,
								value: countryCode
							}
						]
					},
					properties: ['countryCode', 'name', 'regionCode']
				})
			)
			.pipe(map((data: any) => ModelHelper.mapArray<Region>(Region, data)));
	}

	/**
	 * Get region options
	 */
	public getRegionOptions(countryCode: string, refresh: boolean = false): Observable<Option[]> {
		// Check country code
		if (!countryCode) {
			return of([]);
		}

		// Use cache
		if (!refresh && this._regionOptions[countryCode]) {
			return of(this._regionOptions[countryCode]);
		}

		// Get region options from API
		return this.getRegionList(countryCode).pipe(
			map((regionList) => {
				// Init
				const regionOptions: Option[] = [];

				// Process all regions
				for (const region of regionList) {
					// Create option
					const option = new Option();
					option.id = region.regionCode;
					option.label = region.name;

					// Push option to region options
					regionOptions.push(option);
				}

				// Cache and return result
				return (this._regionOptions[countryCode] = regionOptions);
			})
		);
	}

	/**
	 * Translations for list range enum
	 *
	 * @param options
	 */
	public translateOptions(options: Option[]) {
		// Check
		if (!Array.isArray(options)) {
			return options;
		}

		// Translate
		const translatedOptions = _.cloneDeep(options);
		for (const option of translatedOptions) {
			switch (option.label) {
				case 'german':
					option.label = this.translateService.instant('optionsService.label.german');
					break;
				case 'Germany':
					option.label = this.translateService.instant('countries.germany');
					break;
				case 'The Bahamas':
					option.label = this.translateService.instant('countries.bahamas');
					break;
				case 'The Gambia':
					option.label = this.translateService.instant('countries.gambia');
					break;
				case 'COMPANY_PRESETS':
					option.label = this.translateService.instant('listranges.COMPANY_PRESETS');
					break;
				case 'ALL':
					option.label = this.translateService.instant('listranges.ALL');
					break;
				case 'THIRTY_DAYS':
					option.label = this.translateService.instant('listranges.THIRTY_DAYS');
					break;
				case 'THREE_MONTHS':
					option.label = this.translateService.instant('listranges.THREE_MONTHS');
					break;
				case 'TWELVE_MONTHS':
					option.label = this.translateService.instant('listranges.TWELVE_MONTHS');
					break;
				case 'SIX_MONTHS':
					option.label = this.translateService.instant('listranges.SIX_MONTHS');
					break;
			}
		}

		// Return translated options
		return translatedOptions;
	}
}
